import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { visuallyHidden } from '@mui/utils';
import { useState, useMemo, useEffect} from 'react';
import Skeleton from '@mui/material/Skeleton';
import { Badge, IconButton, Tooltip, useMediaQuery } from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import Tab from '@mui/material/Tab';
import Avatar from '@mui/material/Avatar';
import Paper from '@mui/material/Paper';
import AddIcon from '@mui/icons-material/Add';


function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}


function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
// stableSort() brings sort stability to non-modern browsers (notably IE11). If you
// only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
// with exampleArray.slice().sort(exampleComparator)
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}



const NBAShowdownRosterIds = [{
  rosterSlotId: 475,
  label: 'UTIL',
},
{
  rosterSlotId: 476,
  label: 'CPT',
}]


const NBAClassicRosterIds = [
{
  rosterSlotId: 458,
  label: 'PG',
},
{
  rosterSlotId: 459,
  label: 'SG',
},
{
  rosterSlotId: 460,
  label: 'SF',
},
{
  rosterSlotId: 461,
  label: 'PF',
},
{
  rosterSlotId: 462,
  label: 'C',
},
{
  rosterSlotId: 464,
  label: 'G',
},
{
  rosterSlotId: 463,
  label: 'F',
},
{
  rosterSlotId: 465,
  label: 'UTIL',
},
]

const headCells = [
  {
    id: 'images',
    numeric: false,
    label: '',
  },
  {
    id: 'Position',
    numeric: false,
    label: 'POS',
  },
  {
    id: 'Player',
    numeric: false,
    label: 'Player',
  },
  {
    id: 'Team',
    numeric: false,
    label: 'Team',
  },
  {
    id: 'DkFPTs',
    numeric: false,
    label: 'FPTs',
  },
  {
    id: 'ExpectedMins',
    numeric: false,
    label: 'EXP Mins',
  },
  {
    id: 'ExpectedPts',
    numeric: false,
    label: 'EXP FPTs',
  },
  {
    id: 'Salary',
    numeric: false,
    label: 'Salary',
  },
  {
    id: 'actions',
    numeric: false,
    label: '',
  },
];

function EnhancedTableHead(props) {
  const { order, orderBy,  onRequestSort } =
    props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

function ShowBadge({ status, children }) {
  let content;

  if (status === 'None' || status === '' || status === null) {
    content = children;
  } else if (status === 'OUT') {
    content = <Badge badgeContent={'O'} color='error'>{children}</Badge>;
  } else if (status === 'Q') {
    content = <Badge badgeContent={'Q'} color='info'>{children}</Badge>;
  } else {
    content = children;
  }
  return <>{content}</>;
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};


export default function FantasyTable({dataInfo, type, isLoading}) {

  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const isMediumScreen = useMediaQuery('(min-width:601px) and (max-width:960px)');
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('calories');
  const [rosterId, setRosterId] = useState('');
  const [roster, setRoster] = useState([])

  const [data, setData] = useState([])
  const page = 0;
  const rowsPerPage = 400;

  const getTableMaxHeight = () => {
    if (isSmallScreen) {
      return '450px'; // Set your maxHeight for small screens
    } else if (isMediumScreen) {
      return '500px'; // Set your maxHeight for medium screens
    } else {
      return '550px'; // Set your default maxHeight for larger screens
    }
  };

  const handleChange = (event, newValue) => {
    setRosterId(newValue);
  };

  useEffect(() => {if (type === 'Classic') {
    setRoster(NBAClassicRosterIds)
    setRosterId(458)
  } else {
    setRoster(NBAShowdownRosterIds)
    setRosterId(475)
  }}, [type])

  useEffect(() => {
    setData(dataInfo)
  }, [dataInfo])

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const visibleRows = useMemo(
    () =>
      stableSort(data, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [order, orderBy, page, rowsPerPage, data],
  );

  return (
    <Paper square={false} elevation={3}>
    <TabContext value={rosterId}>
      <TabList onChange={handleChange} aria-label="lab API tabs example">
        {roster.map((rosterItem) => {
          return <Tab label={rosterItem.label} value={rosterItem.rosterSlotId} />
        }
        )}
    </TabList>
    <TableContainer sx={{maxHeight: getTableMaxHeight()}}>
          <Table stickyHeader aria-labelledby="tableTitle"size='small'>
            <EnhancedTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} rowCount={data.length}/>
            <TableBody>
              {isLoading ? [0, 1, 2, 3, 4, 5, 6].map(() => {return<TableCell><Skeleton variant="rectangular" width={100} height={40} /></TableCell>}): 
              visibleRows.map((row) => {
                if (row.rosterSlotId === rosterId || rosterId === '' || rosterId === null){
                return (
                  <TableRow hover role="checkbox" key={row.DkID}>
                    <TableCell>
                      <ShowBadge status={row.Status}>
                        <Avatar alt={row.Player}  src={row.image} />
                      </ShowBadge>
                    </TableCell>
                    <TableCell>{row.Position}</TableCell>
                    <TableCell component="th" scope="row" padding="1">{row.Player}</TableCell>
                    <TableCell>{row.Team}</TableCell>
                    <TableCell>{row.DkFPTs}</TableCell>
                    <TableCell>{row.ExpectedMins}</TableCell>
                    <TableCell>{row.ExpectedPts}</TableCell>
                    <TableCell>${row.Salary.toLocaleString('en-us')}</TableCell>
                    <Tooltip title="Doesn't work yet."><TableCell><IconButton size="small" color="success"><AddIcon fontSize="inherit" /></IconButton></TableCell></Tooltip>
                  </TableRow>
              )} return null})}
            </TableBody>
          </Table>
        </TableContainer>
        </TabContext>
        </Paper>
  );
}